import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { catchError, filter, first, map } from 'rxjs/operators';
import { UrlHelperService } from '../../core';
import { AUTH_CONFIG, AuthConfig } from '../config/auth.config';
import { AuthStateEnum } from '../interfaces/auth-state.enum';
import { AuthService } from '../services/auth.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthorizedGuard implements CanActivate, CanActivateChild {
  constructor(
    @Inject(AUTH_CONFIG) private config: AuthConfig,
    private authService: AuthService,
    private router: Router,
    private urlHelper: UrlHelperService,
  ) {}

  /**
   * Check navigation
   * Disallow: Anonymous
   * Allow: Authorized
   * Allow: On error
   */
  public canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.check(next, state);
  }

  public canActivateChild(): Observable<boolean | UrlTree> {
    return this.check();
  }

  private check(_?: ActivatedRouteSnapshot | undefined, state?: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authService.listen().pipe(
      filter(() => !this.authService.isLoadingState()),
      first(),
      untilDestroyed(this),
      map((state: AuthStateEnum) => state === AuthStateEnum.AUTHORIZED),
      map((allowed: boolean) => {
        if (allowed) return allowed;
        const unAuthorizedUrl = this.urlHelper.localizeUrl(this.config.unauthorizedRoute);
        if (state?.url) {
          const currentUrl = encodeURIComponent(state?.url);
          return this.router.parseUrl(`${unAuthorizedUrl}?redirect=${currentUrl}`);
        }
        return this.router.parseUrl(unAuthorizedUrl);
      }),
      catchError(() => of(true)),
    );
  }
}
